import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, ArrowLeft, Building2, UserMinus, X, CheckSquare, Square } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import MonthPicker, { currentMonthYm, humanMonth } from "@/components/MonthPicker";

const EXIT_TYPES = ["Absconding", "Resignation", "Termination", "End of Contract", "Retirement", "Death"];

function BulkExitModal({ open, onClose, employees, onSubmitted }) {
  const today = new Date().toISOString().slice(0, 10);
  const [exitType, setExitType] = useState("Absconding");
  const [resignationDate, setResignationDate] = useState(today);
  const [requestedLwd, setRequestedLwd] = useState(today);
  const [noticePeriodDays, setNoticePeriodDays] = useState(0);
  const [rehire, setRehire] = useState("No");
  const [reason, setReason] = useState("Bulk mark from Zero-Attendance dashboard");
  const [notes, setNotes] = useState("Auto-flagged — no attendance and/or missing from last 2 payroll runs");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;
  const doSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/dashboard/zero-attendance/bulk-exit", {
        employee_ids: employees.map(e => e.id),
        exit_type: exitType,
        resignation_date: resignationDate,
        requested_lwd: requestedLwd,
        notice_period_days: Number(noticePeriodDays) || 0,
        exit_reason_category: reason,
        notes,
        rehire_eligibility: rehire,
      });
      toast.success(`${data.created}/${data.requested} exit requests submitted for approval`);
      if (data.failed > 0) {
        toast.warning(`${data.failed} failed — check console`, { description: "Some employees may already have a draft exit." });
        console.warn("Bulk exit errors:", data.errors);
      }
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk exit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="bulk-exit-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <UserMinus className="h-5 w-5 text-red-600" />
            <h2 className="font-bold text-lg text-slate-900" style={{ fontFamily: "Chivo" }}>
              Mark {employees.length} as Left — via Approval
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="bulk-exit-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
            Each employee will get its own PENDING approval request. Super Admin must approve them individually before the exit is applied. Employees stay Working until approved + LWD arrives.
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Exit Type</label>
              <select
                data-testid="bulk-exit-type"
                value={exitType}
                onChange={(e) => setExitType(e.target.value)}
                className="w-full mt-1 border border-slate-200 rounded px-2 py-1.5 text-sm"
              >
                {EXIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Rehire Eligibility</label>
              <select
                data-testid="bulk-exit-rehire"
                value={rehire}
                onChange={(e) => setRehire(e.target.value)}
                className="w-full mt-1 border border-slate-200 rounded px-2 py-1.5 text-sm"
              >
                <option>Yes</option><option>No</option><option>Conditional</option><option>Pending</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Resignation Date</label>
              <input
                type="date"
                data-testid="bulk-exit-resignation"
                value={resignationDate}
                onChange={(e) => setResignationDate(e.target.value)}
                className="w-full mt-1 border border-slate-200 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Requested LWD</label>
              <input
                type="date"
                data-testid="bulk-exit-lwd"
                value={requestedLwd}
                onChange={(e) => setRequestedLwd(e.target.value)}
                className="w-full mt-1 border border-slate-200 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Notice Period (days)</label>
              <input
                type="number"
                min="0"
                data-testid="bulk-exit-notice"
                value={noticePeriodDays}
                onChange={(e) => setNoticePeriodDays(e.target.value)}
                className="w-full mt-1 border border-slate-200 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Reason Category</label>
              <input
                data-testid="bulk-exit-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full mt-1 border border-slate-200 rounded px-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Notes</label>
            <textarea
              data-testid="bulk-exit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full mt-1 border border-slate-200 rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div className="max-h-32 overflow-auto bg-slate-50 rounded p-2 text-[11px] space-y-0.5 border border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">
              {employees.length} selected employee(s):
            </div>
            {employees.map(e => (
              <div key={e.id} className="text-slate-700">
                <span className="font-mono text-slate-500">{e.punch_id}</span> · {e.name}
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="bulk-exit-cancel">
            Cancel
          </Button>
          <Button
            onClick={doSubmit}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700"
            data-testid="bulk-exit-submit"
          >
            {submitting ? "Submitting…" : `Submit ${employees.length} for Approval`}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardZeroAttendance() {
  const [params] = useSearchParams();
  const month = params.get("month") || currentMonthYm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      setSelected(new Set());
      try {
        const { data } = await api.get(`/dashboard/zero-attendance?month=${month}`);
        if (live) setData(data);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [month, refreshTick]);

  const rows = data?.rows || [];
  const filtered = useMemo(() => {
    if (!filter.trim()) return rows;
    const q = filter.toLowerCase();
    return rows.filter(r =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.punch_id || "").toLowerCase().includes(q) ||
      (r.employee_code || "").toLowerCase().includes(q) ||
      (r.department_name || "").toLowerCase().includes(q) ||
      (r.unit_name || "").toLowerCase().includes(q)
    );
  }, [rows, filter]);

  const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));
  const someSelected = filtered.some(r => selected.has(r.id));

  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) filtered.forEach(r => next.delete(r.id));
    else filtered.forEach(r => next.add(r.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectedRows = rows.filter(r => selected.has(r.id));

  return (
    <div className="space-y-5" data-testid="dashboard-zero-attendance-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-slate-400 hover:text-slate-700" data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <AlertTriangle className="h-7 w-7 text-amber-600" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">HR Snapshot · Ghost Suspects</div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
              Zero Attendance · {humanMonth(month)}
            </h1>
          </div>
        </div>
        <MonthPicker testId="zero-att-month" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="zero-att-count-strip">
        <div className="flex items-center gap-3">
          <div className="text-3xl font-bold text-amber-600" style={{ fontFamily: "Chivo" }}>
            {loading ? "…" : (data?.count ?? 0)}
          </div>
          <div className="text-sm text-slate-600 max-w-xl leading-snug">
            Working employees with <b>zero attendance</b> in {humanMonth(month)} <b>or</b> missing / zero-salary in the last 2 payroll runs.
          </div>
        </div>
        <input
          data-testid="zero-att-filter"
          placeholder="Filter by name, punch id, dept, unit…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="text-xs border border-slate-200 rounded px-3 py-2 w-64 focus:outline-none focus:ring-1 focus:ring-red-400"
        />
      </div>

      {!loading && (data?.count ?? 0) === 0 && (
        <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center text-sm text-slate-400">
          No zero-attendance employees flagged for {humanMonth(month)}.
        </div>
      )}

      {!loading && (data?.count ?? 0) > 0 && (
        <>
          {someSelected && (
            <div className="sticky top-2 z-10 bg-red-600 text-white rounded-lg p-3 flex items-center justify-between shadow-lg" data-testid="bulk-action-bar">
              <div className="text-sm font-medium">
                {selectedRows.length} selected
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-xs bg-white/10 hover:bg-white/20 rounded px-3 py-1.5"
                  data-testid="clear-selection"
                >
                  Clear
                </button>
                <button
                  onClick={() => setShowModal(true)}
                  className="text-xs bg-white text-red-600 font-semibold rounded px-3 py-1.5 hover:bg-red-50"
                  data-testid="open-bulk-exit-modal"
                >
                  Mark {selectedRows.length} as Left →
                </button>
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 w-8">
                    <button onClick={toggleAll} className="text-slate-500 hover:text-red-600" data-testid="toggle-all">
                      {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                    </button>
                  </th>
                  <th className="px-3 py-2">Employee</th>
                  <th className="px-3 py-2">Punch ID / Code</th>
                  <th className="px-3 py-2">Department</th>
                  <th className="px-3 py-2">Unit</th>
                  <th className="px-3 py-2">DOJ</th>
                  <th className="px-3 py-2">CTC</th>
                  <th className="px-3 py-2">Flags</th>
                  <th className="px-3 py-2">Att Days</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const isSel = selected.has(e.id);
                  return (
                    <tr
                      key={e.id}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${isSel ? "bg-red-50" : ""}`}
                      data-testid={`zero-att-row-${e.id}`}
                    >
                      <td className="px-3 py-2">
                        <button
                          onClick={() => toggleOne(e.id)}
                          className="text-slate-500 hover:text-red-600"
                          data-testid={`select-${e.id}`}
                        >
                          {isSel ? <CheckSquare className="h-4 w-4 text-red-600" /> : <Square className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/employees/${e.id}`} className="font-semibold text-slate-900 hover:text-red-600">
                          {e.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">
                        <div>{e.punch_id}</div>
                        <div className="text-slate-400">{e.employee_code}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-700 text-xs">{e.department_name || "—"}</td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        <div className="flex items-center gap-1">
                          <Building2 className="h-3 w-3 text-slate-400" />
                          {e.unit_name || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">{e.doj}</td>
                      <td className="px-3 py-2 font-mono text-sm text-slate-900">
                        ₹{(e.ctc || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(e.reasons || []).includes("no_attendance") && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[9px]">
                              No Attendance
                            </Badge>
                          )}
                          {(e.reasons || []).includes("missing_from_payroll") && (
                            <Badge className="bg-red-100 text-red-800 border border-red-200 text-[9px]">
                              Missing from Payroll
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">
                        {e.attendance_paid_days || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="p-6 text-center text-xs text-slate-400">
                No employees match &quot;{filter}&quot;
              </div>
            )}
          </div>
        </>
      )}

      <BulkExitModal
        open={showModal}
        onClose={() => setShowModal(false)}
        employees={selectedRows}
        onSubmitted={() => {
          setShowModal(false);
          setSelected(new Set());
          setRefreshTick(t => t + 1);
        }}
      />
    </div>
  );
}
